/**
 * @element tooltip-custom
 */
class TooltipCustom extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {number|null} */
  #hideTimeout = null;

  /** @type {number} */
  #positionRAF = 0;

  /** @type {boolean} */
  #isOpen = false;

  /** @type {boolean} */
  #openedByClick = false;

  static HIDE_DELAY = 100;
  static VIEWPORT_MARGIN = 8;

  /** @returns {HTMLButtonElement|null} */
  get trigger() {
    return this.querySelector('.tooltip__trigger');
  }

  /** @returns {HTMLElement|null} */
  get tooltip() {
    return this.querySelector('[role="tooltip"]');
  }

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('mouseenter', this.#handleMouseEnter, { signal });
    this.addEventListener('mouseleave', this.#handleMouseLeave, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });

    this.trigger?.addEventListener('click', this.#handleClick, { signal });

    document.addEventListener('click', this.#handleDocumentClick, { signal });
    document.addEventListener('keydown', this.#handleDocumentKeyDown, { signal });
    document.addEventListener('scroll', this.#schedulePositionUpdate, {
      signal,
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', this.#schedulePositionUpdate, { signal });

    this.#applyPreferredPosition();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#clearHideTimeout();
    cancelAnimationFrame(this.#positionRAF);
  }

  #show() {
    if (this.#isOpen) return;
    this.#clearHideTimeout();
    this.#isOpen = true;
    this.#computePosition();
    this.setAttribute('aria-state', 'open');
    this.trigger?.setAttribute('aria-expanded', 'true');
  }

  #hide() {
    if (!this.#isOpen) return;
    this.#isOpen = false;
    this.#openedByClick = false;
    this.removeAttribute('aria-state');
    this.trigger?.setAttribute('aria-expanded', 'false');
  }

  #scheduleHide() {
    this.#clearHideTimeout();
    this.#hideTimeout = window.setTimeout(() => this.#hide(), TooltipCustom.HIDE_DELAY);
  }

  #clearHideTimeout() {
    if (this.#hideTimeout !== null) {
      clearTimeout(this.#hideTimeout);
      this.#hideTimeout = null;
    }
  }

  #applyPreferredPosition() {
    const tooltip = this.tooltip;
    if (!tooltip) return;
    tooltip.dataset.position = this.dataset.preferredPosition || 'bottom-center';
  }

  #computePosition() {
    const tooltip = this.tooltip;
    const trigger = this.trigger;
    if (!tooltip || !trigger) return;

    const preferred = this.dataset.preferredPosition || 'bottom-center';
    const triggerRect = trigger.getBoundingClientRect();
    const tooltipWidth = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;
    const margin = TooltipCustom.VIEWPORT_MARGIN;

    const available = {
      top: triggerRect.top - margin,
      bottom: window.innerHeight - triggerRect.bottom - margin,
      left: triggerRect.left - margin,
      right: window.innerWidth - triggerRect.right - margin,
    };

    tooltip.dataset.position = this.#resolvePosition(
      preferred,
      tooltipWidth,
      tooltipHeight,
      available
    );
  }

  /**
   * @param {string} preferred
   * @param {number} width
   * @param {number} height
   * @param {{ top: number, bottom: number, left: number, right: number }} available
   * @returns {string}
   */
  #resolvePosition(preferred, width, height, available) {
    if (preferred.startsWith('bottom')) {
      if (available.bottom < height && available.top >= height) {
        return preferred.replace('bottom', 'top');
      }
      return preferred;
    }

    if (preferred.startsWith('top')) {
      if (available.top < height && available.bottom >= height) {
        return preferred.replace('top', 'bottom');
      }
      return preferred;
    }

    if (preferred === 'left') {
      if (available.left < width && available.right >= width) return 'right';
      return preferred;
    }

    if (preferred === 'right') {
      if (available.right < width && available.left >= width) return 'left';
      return preferred;
    }

    return preferred;
  }

  #schedulePositionUpdate = () => {
    if (!this.#isOpen) return;
    cancelAnimationFrame(this.#positionRAF);
    this.#positionRAF = requestAnimationFrame(() => this.#computePosition());
  };

  /** @param {MouseEvent} _event */
  #handleMouseEnter = (_event) => {
    if (this.#openedByClick) return;
    this.#clearHideTimeout();
    this.#show();
  };

  /** @param {MouseEvent} _event */
  #handleMouseLeave = (_event) => {
    if (this.#openedByClick) return;
    this.#scheduleHide();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    event.preventDefault();
    if (this.#isOpen) {
      this.#hide();
    } else {
      this.#openedByClick = true;
      this.#show();
    }
  };

  /** @param {FocusEvent} _event */
  #handleFocusIn = (_event) => {
    this.#clearHideTimeout();
    this.#show();
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (this.contains(/** @type {Node} */ (event.relatedTarget))) return;
    this.#scheduleHide();
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#isOpen) {
      event.preventDefault();
      this.#hide();
      this.trigger?.focus();
    }
  };

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (!this.#isOpen || !this.#openedByClick) return;
    if (!this.contains(/** @type {Node} */ (event.target))) {
      this.#hide();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleDocumentKeyDown = (event) => {
    if (event.key === 'Escape' && this.#isOpen && this.contains(document.activeElement)) {
      this.#hide();
      this.trigger?.focus();
    }
  };
}

if (!customElements.get('tooltip-custom')) {
  customElements.define('tooltip-custom', TooltipCustom);
}
